/**
 * Razorpay Checkout loader + typed wrapper.
 *
 * The Razorpay script is loaded lazily (only when a patient actually
 * reaches the payment page) and a single script tag is reused across
 * opens. Types are minimal — only what we use.
 */

export interface RazorpayHandlerResponse {
  razorpay_order_id: string;
  razorpay_payment_id: string;
  razorpay_signature: string;
}

export interface RazorpayCheckoutOptions {
  key: string;
  amount: number; // paise
  currency: string; // "INR"
  name: string;
  description?: string;
  order_id: string;
  image?: string;
  prefill?: { name?: string; email?: string; contact?: string };
  notes?: Record<string, string>;
  theme?: { color?: string };
  handler: (resp: RazorpayHandlerResponse) => void;
  modal?: { ondismiss?: () => void; escape?: boolean };
}

interface RazorpayInstance {
  open: () => void;
  close: () => void;
  on: (
    event: "payment.failed",
    cb: (resp: { error: { description?: string; reason?: string } }) => void,
  ) => void;
}

type RazorpayCtor = new (options: RazorpayCheckoutOptions) => RazorpayInstance;

declare global {
  interface Window {
    Razorpay?: RazorpayCtor;
  }
}

const SCRIPT_URL = "https://checkout.razorpay.com/v1/checkout.js";
let loadPromise: Promise<void> | null = null;

export function loadRazorpay(): Promise<void> {
  if (typeof window === "undefined") return Promise.reject(new Error("SSR"));
  if (window.Razorpay) return Promise.resolve();
  if (loadPromise) return loadPromise;

  loadPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      `script[src="${SCRIPT_URL}"]`,
    );
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () =>
        reject(new Error("Payment checkout could not be loaded")),
      );
      return;
    }
    const s = document.createElement("script");
    s.src = SCRIPT_URL;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("Payment checkout could not be loaded"));
    document.body.appendChild(s);
  });

  return loadPromise;
}

export async function openRazorpayCheckout(
  options: RazorpayCheckoutOptions,
): Promise<void> {
  await loadRazorpay();
  if (!window.Razorpay) throw new Error("Payment checkout is not available");
  const rzp = new window.Razorpay(options);
  rzp.on("payment.failed", (resp) => {
    // Let the page pick up failure via its own state — simple console.log
    // here so developer sees the reason. Page binds handler via options.
    console.warn("Payment failed:", resp?.error);
  });
  rzp.open();
}
